import { QueryClient, type QueryClientConfig } from "@tanstack/react-query";

// Tuned for an internal tool rather than TanStack Query's out-of-the-box
// (public-app-oriented) defaults: data changes infrequently relative to a
// public consumer app and requests go to trusted internal APIs, so we can
// afford to be calmer about refetching/retrying than the framework
// defaults (which assume a flaky public network and refetch aggressively
// on every window focus).
const DEFAULT_STALE_TIME_MS = 60_000; // 1 minute
const DEFAULT_GC_TIME_MS = 5 * 60_000; // 5 minutes
const DEFAULT_QUERY_RETRY = 1;
const DEFAULT_MUTATION_RETRY = 0;

/**
 * Creates a `QueryClient` with defaults sane for an internal tool. Pass
 * `config` to override or extend any of the defaults — anything you
 * provide under `config.defaultOptions.queries` / `.mutations` wins over
 * the values set here.
 *
 * @example
 * <QueryClientProvider client={createQueryClient()}>
 */
export function createQueryClient(
  config: QueryClientConfig = {},
): QueryClient {
  return new QueryClient({
    ...config,
    defaultOptions: {
      ...config.defaultOptions,
      queries: {
        staleTime: DEFAULT_STALE_TIME_MS,
        gcTime: DEFAULT_GC_TIME_MS,
        retry: DEFAULT_QUERY_RETRY,
        refetchOnWindowFocus: false,
        ...config.defaultOptions?.queries,
      },
      mutations: {
        retry: DEFAULT_MUTATION_RETRY,
        ...config.defaultOptions?.mutations,
      },
    },
  });
}
